import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { AuthStatus } from '../hooks/useAuth';
import type { User } from 'firebase/auth';

type AuthContextType = {
  user: User | null;
  status: AuthStatus;
  error: string | null;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (email: string, password: string) => Promise<User>;
  signInWithToken: (token: string) => Promise<User | null>;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { user, status, error, signOut, signIn, signUp, signInWithToken, isLoading } = useAuth();

  const isAuthenticated = status === 'authenticated' && Boolean(user);

  return (
    <AuthContext.Provider value={{ 
      user, 
      status, 
      error, 
      isAuthenticated,
      signOut,
      signIn,
      signUp,
      signInWithToken,
      isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
};
